import type { EquivalentDomainGroupWire } from '@features/equivalent-domains/api/equivalentDomainsApi'
import { extractHost, findGroupForHost } from '@features/equivalent-domains/services/domainMatcher'
import type { VaultItem } from '../types'

/**
 * Vault 一覧の表示行: 個別アイテム or ドメイングループの 2 種。
 *
 * #settings/domain で登録した「ドメインルール」(同じパスワードを共有するドメインの集合)
 * に属する login アイテムを、Vault 一覧で 1 行に統合表示するためのモデル。
 * `group` 行は折りたたみ可能で、展開すると `items` を入れ子の `item` 行として描画する。
 */
export type VaultListEntry =
  | { kind: 'item'; item: VaultItem }
  | { kind: 'group'; group: EquivalentDomainGroupWire; items: readonly VaultItem[] }

/**
 * login アイテムが属するドメイングループ id を返す (該当無し / 非 login は null)。
 * 複数 URL を持つ場合は最初に group へ hit した URL を採用する。
 */
export const groupIdForItem = (
  item: VaultItem,
  groups: readonly EquivalentDomainGroupWire[],
): string | null => {
  if (item.payload.type !== 'login') return null
  for (const u of item.payload.uris ?? []) {
    const host = extractHost(u.uri)
    if (host === null) continue
    const group = findGroupForHost(host, groups)
    if (group) return group.id
  }

  return null
}

/**
 * Vault 一覧アイテムを表示用の `VaultListEntry[]` に変換する。
 *
 * - `mergeEnabled` が false / group 未登録なら、全アイテムを `item` 行で返す (順序保持)。
 * - 同じグループに **2 件以上** 属するアイテムは 1 つの `group` 行に統合する
 *   (1 件のみのグループは統合する意味が無いので `item` 行のまま)。
 * - グループ行は、その先頭メンバーが現れた位置に挿入する (元の並び順を尊重)。
 */
export const buildVaultListEntries = (
  items: readonly VaultItem[],
  groups: readonly EquivalentDomainGroupWire[],
  mergeEnabled: boolean,
): VaultListEntry[] => {
  if (!mergeEnabled || groups.length === 0) {
    return items.map(item => ({ kind: 'item', item }))
  }

  const groupById = new Map(groups.map(g => [g.id, g]))

  // groupId → メンバー (元の並び順)。
  const membersByGroup = new Map<string, VaultItem[]>()
  for (const item of items) {
    const gid = groupIdForItem(item, groups)
    if (gid === null) continue
    const list = membersByGroup.get(gid)
    if (list) list.push(item)
    else membersByGroup.set(gid, [item])
  }

  const entries: VaultListEntry[] = []
  const emitted = new Set<string>()
  for (const item of items) {
    const gid = groupIdForItem(item, groups)
    const members = gid !== null ? membersByGroup.get(gid) : undefined

    // グループに 2 件以上属するメンバーは group 行へ統合。
    if (gid !== null && members && members.length >= 2) {
      const group = groupById.get(gid)
      if (group) {
        if (!emitted.has(gid)) {
          emitted.add(gid)
          entries.push({ kind: 'group', group, items: members })
        }
        continue
      }
    }

    entries.push({ kind: 'item', item })
  }

  return entries
}
